import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Ticket, Loader2, X } from "lucide-react";

export default function TicketSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const canProfile = ["owner", "manager"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setRes(data);
        setError(false);
        setOpen(true);
      } catch {
        setRes({ stays: [], tenants: [] });
        setError(true);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const empty = res && !res.stays.length && !res.tenants.length;

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Ticket className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
      <Input
        data-testid="ticket-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search ticket # or company"
        className="h-11 border-slate-700 bg-slate-900 pl-9 pr-9"
      />
      {loading && <Loader2 className="absolute right-3 top-3.5 h-4 w-4 animate-spin text-slate-500" />}
      {!loading && q && (
        <button data-testid="ticket-search-clear" onClick={() => setQ("")} className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-200">
          <X className="h-4 w-4" />
        </button>
      )}
      {res && open && (
        <div data-testid="ticket-search-results" className="absolute z-50 mt-2 max-h-96 w-full overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 shadow-2xl">
          {empty && (
            <p data-testid="ticket-search-empty" className={`p-4 text-sm ${error ? "text-red-400" : "text-slate-500"}`}>
              {error ? "Search failed. Try again." : "No matches."}
            </p>
          )}
          {res.tenants.length > 0 && <Header label="Monthly tenants" />}
          {res.tenants.map((t) => (
            <Result
              key={t.id}
              testid={`search-tenant-${t.id}`}
              ticket={t.ticketNumber}
              title={t.companyName}
              sub={`${t.lotName} · $${(t.monthlyRate || 0).toFixed(0)}/mo · ${t.status}`}
              to={canProfile && t.customerId ? `/customers/${t.customerId}` : null}
            />
          ))}
          {res.stays.length > 0 && <Header label="Stays" />}
          {res.stays.map((s) => (
            <Result
              key={s.id}
              testid={`search-stay-${s.id}`}
              ticket={s.ticketNumber}
              title={s.companyName || s.driverName || "—"}
              sub={`${s.lotName} · Spot #${s.spotNumber ?? "—"} · ${s.tenancyType} · ${s.status === "Closed" ? "Past" : "Active"} · ${new Date(s.checkInAt).toLocaleDateString()}`}
              to={canProfile ? `/customers/${s.customerId}` : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ label }) {
  return <p className="bg-slate-950 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>;
}

function Result({ testid, ticket, title, sub, to }) {
  const body = (
    <div className="px-4 py-3 hover:bg-slate-800">
      <p className="font-data text-sm font-bold text-[#d946ef]">{ticket || "—"}</p>
      <p className="font-semibold text-slate-100">{title}</p>
      <p className="text-xs text-slate-400">{sub}</p>
    </div>
  );
  return (
    <div data-testid={testid} className="border-t border-slate-800">
      {to ? <Link to={to}>{body}</Link> : body}
    </div>
  );
}
